import React, { useState, useEffect } from 'react';
import { Map as MapGL } from 'react-map-gl';
import { DeckGL } from 'deck.gl';
import { GeoJsonLayer } from '@deck.gl/layers';
import * as turf from '@turf/turf';
import chroma from 'chroma-js';

function hexToRGBA(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b, 255];  // Alpha set to 255 (fully opaque)
}

export const maturityClassColors = {
  'MatureVine': hexToRGBA('#3A9D44'),
  'WeakVine': hexToRGBA('#e6ae1e'),
  'MissingVine': hexToRGBA('#b5b5b5'),
  'Replant': hexToRGBA('#bfae6b'),
  'RootStock': hexToRGBA('#8c6c30'),
  // 'OutOfRow': hexToRGBA('#ffa600'),
  'YoungVine': hexToRGBA('#8ABB3D'),
  'No Data': hexToRGBA('#000000'),
};

export const discreteColormapAbbrevs = {
  'MatureVine': 'Strong',
  'WeakVine': 'Weak',
  'MissingVine': 'No Vine',
  'Replant': 'Replant',
  'RootStock': 'Root',
  // 'OutOfRow': 'Out',
  'YoungVine': 'Young',
  'No Data': 'No Data',
};

// Calculate new viewState when new block or bucket selected
export const updateViewState = async (blockData, viewState, setViewState) => {
  if (blockData) {
    const bbox = turf.bbox(blockData);
    const centroid = turf.centroid(blockData);
    const cent_long = centroid.geometry.coordinates[0];
    const cent_lat = centroid.geometry.coordinates[1];
    const width = Math.abs(bbox[0] - bbox[2]);
    const height = Math.abs(bbox[1] - bbox[3]);
    const maxDim = Math.max(width, height);
    const zoomScale = 360;
    const zoom = -Math.log2(maxDim / zoomScale) + 0.2;

    setViewState({
      ...viewState,
      latitude: cent_lat,
      longitude: cent_long,
      zoom: zoom,
      bearing: 0,
      pitch: 0,
    });;
  } else {
    console.error("Invalid block data: missing latitude and/or longitude");
  }
};

export const Map = ({ viewState, onViewStateChange, controller, layers }) => (

  <DeckGL
    viewState={viewState}
    onViewStateChange={onViewStateChange}
    controller={controller}
    layers={layers}
  >
    <MapGL
      width="100%"
      height="100%"
      mapStyle="mapbox://styles/mapbox/satellite-v9"
      mapboxAccessToken={"pk.eyJ1IjoibWFzb25lYXJsZXMiLCJhIjoiY2xkeXR3bXNyMG5heDNucHJhYWFscnZnbyJ9.A03O6PN1N1u771c4Qqg1SA"}
      {...viewState}
      interactive={false}
    />
  </DeckGL>
);

export const BlockLayer = ({ geoJsonBlockData }) => {
  new GeoJsonLayer({
    id: 'geojson-block-layer',
    data: geoJsonBlockData,
    opacity: 0.1,
    stroked: true,
    filled: true,
    pickable: true,
    getLineColor: [255, 255, 255],
    getFillColor: [255, 255, 255],
  });
};
  
export const VineLayer = ({ 
  geoJsonVineData,
  selectedInsightCategory,
  parseGeoJsonList,
  selectorToGeoJsonNamesDict
  }) => {
    return new GeoJsonLayer({
      id: 'geojson-vine-layer',
      data: geoJsonVineData,
      pickable: true,
      stroked: false,
      filled: true,
      lineWidthScale: 20,
      lineWidthMinPixels: 2,
      getFillColor: f => {
        const selectorName = selectedInsightCategory;
        const geoJsonName = selectorToGeoJsonNamesDict[selectorName];
        return parseGeoJsonList(f.properties[geoJsonName]);
        },
      getRadius: 0.4,
      getLineWidth: 0,
      getElevation: 30,
      updateTriggers: {
        getFillColor: [selectedInsightCategory],
      }
    });
  }
    

export const onHover = (
    setTooltip, 
    selectedInsightCategory, 
    selectedInsightName
  ) => ({object, x, y}) => {

    const tooltipContent = object && {
      html: `<b>${selectedInsightCategory}: ${object.properties[selectedInsightName].toFixed(0)}</b><br><img src='https://demo-user-01.s3.us-west-2.amazonaws.com/tyree_07102022_trunkgrid/${object.properties.img_names_trunk_vo0}' width='163' height='300' display='flex'>`,
      style: {
        backgroundColor: '#fff',
        fontSize: '0.8em',
        padding: '15px'
      }
    };
    const tooltipWidth = 163; // The width you've set for the tooltip image
    const tooltipHeight = 300; // The height you've set for the tooltip image
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const paddingx = 5;
    const paddingy = 27;

    // Calculate horizontal position
    if (x + tooltipWidth + paddingx > windowWidth) {
      x -= tooltipWidth + 2 * paddingx;
    } else {
      x += paddingx;
    }

    // Calculate vertical position
    if (y + tooltipHeight + paddingy > windowHeight) {
      y -= tooltipHeight + 2 * paddingy;
    } else {
      y += paddingy - paddingy;
    }
    setTooltip({ content: tooltipContent, x, y });
  };

  // Function for dynamically creating a point color mapping
  export function useDynamicPointColorMapping(
    geoJsonVineData,
    geoJsonVineDataFull,
    selectedInsightMetricName,
    selectedSliderRange,
    selectedBlockName
  ) {
    const [minMetricValue, setMinMetricValue] = useState(null);
    const [maxMetricValue, setMaxMetricValue] = useState(null);
    const [filteredFeatures, setFilteredFeatures] = useState([]);
    const [colorScale, setColorScale] = useState(null);
    
  
    useEffect(() => {
      if (geoJsonVineData) {
        const allUniqueBlocks = [...new Set(geoJsonVineDataFull.features.map(feature => feature.properties.block_name))];
        const blocksForFilter = selectedBlockName == 'All Blocks' ? allUniqueBlocks : selectedBlockName;
        const filtered_vine_data = {
          ...geoJsonVineDataFull,
          features: geoJsonVineDataFull.features.filter(feature => {
            const value = feature.properties[selectedInsightMetricName];
            const block = feature.properties.block_name;
            return blocksForFilter.includes(block);
          })
        };

        // Generate metricValues from the whole dataset
        const allMetricValues = filtered_vine_data.features.map(
          (f) => f.properties[selectedInsightMetricName]
        );
        allMetricValues.sort((a, b) => a - b); // Ascending sort
    
        // Calculate min and max values from the whole dataset
        const minMetric = percentile(allMetricValues, 2); // 5th percentile
        const maxMetric = percentile(allMetricValues, 98); // 95th percentile
    
        // Create color scale based on min and max values from the whole dataset
        const color = chroma.scale('RdYlBu')
        setColorScale(() => color);
    
        // Filter features based on selectedSliderRange
        const filtered = geoJsonVineData.features.filter((feature) => {
          const value = feature.properties[selectedInsightMetricName];
          return (
            value >= selectedSliderRange[0] && value <= selectedSliderRange[1]
          );
        });
        setFilteredFeatures(filtered);
        setMinMetricValue(minMetric);
        setMaxMetricValue(maxMetric);
      }
    }, [geoJsonVineData, selectedSliderRange, selectedInsightMetricName, selectedBlockName]);
    
    

    return { minMetricValue, maxMetricValue, filteredFeatures, colorScale };
  };

  // Helper function to compute percentiles
function percentile(arr, p) {
  if (arr.length === 0) return 0;
  if (typeof p !== 'number') throw new TypeError('p must be a number');
  if (p <= 0) return arr[0];
  if (p >= 100) return arr[arr.length - 1];

  const index = (arr.length - 1) * p / 100;
  const lower = Math.floor(index);
  const upper = lower + 1;
  const weight = index % 1;

  if (upper >= arr.length) return arr[lower];
  return arr[lower] * (1 - weight) + arr[upper] * weight;
}
